import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { amber, blue, orange, purple } from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";

const SubscriptionPlansSettings = () => {
  const { getAllSubscriptionPlansApiCall } = useAdminContext();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const result = await getAllSubscriptionPlansApiCall();

      if (result?.subscription_plans) {
        setPlans(result.subscription_plans);
      }
    } catch (error) {
      console.error("Error loading subscription plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanColor = (planName) => {
    const name = planName.toLowerCase();
    if (name === "basic") return blue[500];
    if (name === "standard") return blue[600];
    if (name === "premium") return orange[600];
    if (name === "elite") return purple[600];
    return blue[500];
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subscription plans are seeded in the database. These plans match what
          users see in the membership manager. Manage plans through the
          database.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Card elevation={3}>
          <CardHeader
            avatar={<MoneyIcon sx={{ color: amber[600] }} />}
            title="Subscription Plans"
            subheader="Current membership tiers available to users"
            sx={{ bgcolor: amber[50] }}
            action={
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPlans}
                sx={{ color: amber[600], borderColor: amber[600] }}
              >
                Refresh
              </Button>
            }
          />
          <CardContent>
            {plans.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No subscription plans found. Run database seed script.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {plans.map((plan) => (
                  <Grid item xs={12} md={6} lg={3} key={plan.id}>
                    <Card
                      elevation={2}
                      sx={{
                        height: "100%",
                        border: `2px solid ${getPlanColor(plan.name)}`,
                        position: "relative",
                      }}
                    >
                      {plan.is_popular ? (
                        <Chip
                          label="MOST POPULAR"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: orange[600],
                            color: "white",
                            fontWeight: "bold",
                            zIndex: 1,
                          }}
                        />
                      ) : null}
                      <CardContent>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: "bold",
                              color: getPlanColor(plan.name),
                              textTransform: "capitalize",
                              mb: 1,
                            }}
                          >
                            {plan.name}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                            ${plan.price_monthly}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              /month
                            </Typography>
                          </Typography>
                          {plan.max_rooms_per_day && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Up to {plan.max_rooms_per_day} rooms/day
                            </Typography>
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Features:
                        </Typography>
                        <List dense>
                          {plan.features &&
                            plan.features.map((feature, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon
                                    sx={{
                                      color: getPlanColor(plan.name),
                                      fontSize: 20,
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={feature}
                                  primaryTypographyProps={{
                                    variant: "body2",
                                  }}
                                />
                              </ListItem>
                            ))}
                        </List>

                        <Box sx={{ mt: 2 }}>
                          <Chip
                            label={plan.is_active ? "Active" : "Inactive"}
                            size="small"
                            color={plan.is_active ? "success" : "default"}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SubscriptionPlansSettings;
